/**
 * DASHBOARD-BENCHMARK-MINI-CARD
 * showLabel=true (questions): label devant + tooltip simple
 * showLabel=false (heatmap): chip seule + label dans le tooltip
 */
import { faArrowDown, faArrowUp, faCircleNotch, faEquals, faMagnifyingGlassChart } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Link from "@/components/link"
import Chip from "@/components/chip"
import Tooltip from "@/components/tooltip"
import { AccountOptions } from "@/redux/account.types"
import { benchmarkDelta } from "@/features/benchmark/entities/question/model/benchmarkQuestion.utils"
import { t } from "i18next"

interface Props {
  accountOptions: AccountOptions
  score: number
  benchmarkValue?: number | null
  onOpen: () => void
  isLoading?: boolean
  showLabel?: boolean
  // Minimum |diff| (in the same raw units as score/benchmarkValue) to count as above/below
  // average rather than "=" — 0.3 fits the 0-10 scale questions use; NPS callers (-100..100
  // raw) should pass a larger value.
  stepThreshold?: number
}

function getStep(diff: number | null, threshold: number): -1 | 0 | 1 {
  if (diff == null) return 0
  if (diff <= -threshold) return -1
  if (diff >= threshold) return 1
  return 0
}

//Arrow and color of a score against a reference (benchmark, account in the matrix): same rule everywhere
export function getScoreTrend(
  diff: number | null,
  accountOptions: Pick<AccountOptions, "dashboardNightmareColor1" | "dashboardNightmareColor3">,
  threshold = 0.3
) {
  const step = getStep(diff, threshold)
  const green = accountOptions.dashboardNightmareColor3 || "#20CA7E"
  const red = accountOptions.dashboardNightmareColor1 || "#eb5a46"
  return {
    step,
    color: step < 0 ? red : step > 0 ? green : "#9CA3AF",
    icon: step < 0 ? faArrowDown : step > 0 ? faArrowUp : faEquals
  }
}

export default function DashboardBenchmarkMiniCard({
  accountOptions,
  score,
  benchmarkValue,
  onOpen,
  isLoading,
  showLabel = true,
  stepThreshold = 0.3
}: Props) {
  if (isLoading) {
    return (
      <div className="flex items-center gap-2 grey-t">
        <FontAwesomeIcon icon={faCircleNotch} spin />
        <span>{t("benchmark_q_loading")}</span>
      </div>
    )
  }

  //On the values as displayed (one decimal): two equal displays never get different arrows
  const diff = benchmarkValue != null ? benchmarkDelta(score, benchmarkValue, 1) : null
  const { step, color, icon } = getScoreTrend(diff, accountOptions, stepThreshold)
  const label =
    step < 0
      ? t("benchmark_q_step_below_avg")
      : step > 0
        ? t("benchmark_q_step_above_avg")
        : t("benchmark_q_step_average")

  const tooltipText = showLabel
    ? t("benchmark_q_see_detail")
    : [label, t("benchmark_q_see_detail")]

  return (
    <Link onClick={onOpen} isUnderline={false} className="flex items-center gap-3">
      {showLabel && (
        <span className="font-bold grey-t flex items-center gap-2">
          <FontAwesomeIcon icon={faMagnifyingGlassChart} />
          {label}
        </span>
      )}
      <Tooltip text={tooltipText} position="right" usePortal>
        <div style={{ width: 50 }} className="[&_svg]:mr-0 inline-block">
          <Chip icon={icon} color={color} isOutlineColored className="!flex items-center justify-center min-h-[24px] [&_span]:flex [&_svg]:block" onClick={onOpen} />
        </div>
      </Tooltip>
    </Link>
  )
}
